import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs } from '../../../design-system';
import { CustomerOrders } from '../Orders';
import { CustomerDeliveries } from '../Deliveries';
import { CustomerInvoicesPayment } from '../InvoicesPayment';
import { CustomerReturnsTab } from './CustomerReturnsTab';

export type ActivityHubTabId = 'orders' | 'deliveries' | 'invoices' | 'returns';

const ROUTE_FOR_TAB: Record<ActivityHubTabId, string> = {
  orders: '/customer-orders',
  deliveries: '/customer-deliveries',
  invoices: '/customer-invoices',
  returns: '/customer-returns',
};

interface CustomerActivityHubProps {
  initialTab: ActivityHubTabId;
}

/**
 * VP-CUST-003: consolidates the previously-separate CustomerOrders /
 * CustomerDeliveries / CustomerInvoicesPayment pages into a single tabbed
 * hub, plus a new Returns tab (see CustomerReturnsTab). The three original
 * routes (/customer-orders, /customer-deliveries, /customer-invoices) are
 * kept working — App.tsx still mounts each one at its own path, just now
 * rendering this same hub with the matching tab pre-selected, so existing
 * links/bookmarks/nav-menu entries (navConfig.ts, NotificationDropdown,
 * CustomerOrderDetails' back-link, CustomerCart, OutstandingInvoices, the
 * new QuickActions "Reorder" button) all keep working unchanged. A new
 * /customer-returns route was added for the Returns tab since nothing
 * pointed at that URL before.
 */
export const CustomerActivityHub: React.FC<CustomerActivityHubProps> = ({ initialTab }) => {
  const navigate = useNavigate();

  const tabs = [
    { id: 'orders', label: 'Orders', content: <CustomerOrders /> },
    { id: 'deliveries', label: 'Deliveries', content: <CustomerDeliveries /> },
    { id: 'invoices', label: 'Invoices & Payment', content: <CustomerInvoicesPayment /> },
    { id: 'returns', label: 'Returns', content: <CustomerReturnsTab /> },
  ];

  const handleChange = (tabId: string) => {
    const route = ROUTE_FOR_TAB[tabId as ActivityHubTabId];
    if (route) navigate(route, { replace: true });
  };

  return <Tabs tabs={tabs} defaultTabId={initialTab} onChange={handleChange} />;
};

export default CustomerActivityHub;
